import css from "./BrowsePage.sass?inline"
import {Html} from "@opendaw/lib-dom"
import {createElement} from "@opendaw/lib-jsx"
import {CardView} from "@/CardView"
import {Track, TracksList} from "@/api"
import {isDemoted} from "@/demoted"

const className = Html.adoptStyleSheet(css, "BrowsePage")

type Construct = {
    trackList: TracksList
}

const searchableText = ({metadata: {name, artist, tags, description}}: Track): string =>
    [name, artist, description, ...tags].join(" ").toLowerCase()

export const BrowsePage = ({trackList}: Construct) => {
    const sortedTracks = trackList.tracks
        .sort((a, b) => {
            const demotion = Number(isDemoted(a)) - Number(isDemoted(b))
            return demotion !== 0 ? demotion : b.metadata.modified.localeCompare(a.metadata.modified)
        })
    const haystacks = new Map(sortedTracks.map(track => [track, searchableText(track)]))
    const cards = new Map<Track, HTMLElement>()
    let filteredTracks: ReadonlyArray<Track> = sortedTracks

    const playNext = (track: Track) => {
        const nextTrack = filteredTracks[filteredTracks.indexOf(track) + 1]
        if (nextTrack) {
            cards.get(nextTrack)?.querySelector("audio")?.play()
        }
    }
    const emptyMessage: HTMLElement = <p className="empty hidden">No tracks match your filter.</p>
    const input: HTMLInputElement = (
        <input type="search"
               placeholder="Filter by name, artist or tag…"
               autocomplete="off"
               spellcheck={false}
               oninput={() => applyFilter(input.value)}/>
    )
    const applyFilter = (query: string) => {
        const terms = query.toLowerCase().split(/\s+/).filter(term => term.length > 0)
        filteredTracks = sortedTracks.filter(track => {
            const haystack = haystacks.get(track) ?? ""
            return terms.every(term => haystack.includes(term))
        })
        const visible = new Set(filteredTracks)
        cards.forEach((card, track) => card.classList.toggle("hidden", !visible.has(track)))
        emptyMessage.classList.toggle("hidden", filteredTracks.length > 0)
    }
    const filterByTag = (tag: string) => {
        input.value = tag
        applyFilter(tag)
        input.scrollIntoView({behavior: "smooth", block: "nearest"})
    }
    sortedTracks.forEach(track => cards.set(track, CardView({
        track,
        onEnded: () => playNext(track),
        onTagClick: filterByTag
    }) as HTMLElement))
    return (
        <div className={className}>
            <h1>openDAW Music 🎧</h1>
            <p>
                The music on this page is made by the openDAW community.<br/>
                All tracks can be opened and edited in the studio.
            </p>
            <div className="filter">{input}</div>
            {emptyMessage}
            <div className="grid">
                {sortedTracks.map(track => cards.get(track))}
            </div>
        </div>
    )
}
